const BundlePage = {
  root: null,
  config: null,
  handle: '',
  parentVariantId: '',
  catalogPriceCents: 0,
  showSavingsBanner: true,
  moneyFormat: '',
  currency: '',
  cartAddUrl: '/cart/add.js',
  selections: new Map(),
  slotProducts: new Map(),
  isAdding: false,
  addToCartLabels: new Map(),
  mobileSheetOpen: false,
  activeSlotIndex: 0,
  slotObserver: null,
  slotExpanded: new Set(),
  isProgrammaticScroll: false,
  sizeSelectorDocumentBound: false,
  bundleUnavailable: false,
  unavailableModalDismissed: false,
  unavailableModalBound: false,
  unavailableModalFocusReturn: null,

  init: function (rootElement) {
    if (!rootElement) {
      return;
    }

    this.root = rootElement;
    this.cartAddUrl = '/cart/add.js';

    const configScript = document.getElementById(rootElement.dataset.configId || '');
    if (!configScript) {
      return;
    }

    let bootstrap;
    try {
      bootstrap = JSON.parse(configScript.textContent);
    } catch (error) {
      console.warn('BundlePage: invalid bootstrap JSON', error);
      return;
    }

    this.config = bootstrap.config || null;
    this.handle = bootstrap.handle || rootElement.dataset.bundleHandle || '';
    this.parentVariantId = bootstrap.parentVariantId || '';
    this.catalogPriceCents = Number(bootstrap.catalogPriceCents) || 0;
    this.showSavingsBanner = rootElement.dataset.showSavingsBanner !== 'false';
    this.moneyFormat = bootstrap.moneyFormat || '';
    this.currency = bootstrap.currency || '';

    if (bootstrap.cartAddUrl) {
      this.cartAddUrl = bootstrap.cartAddUrl;
    }

    if (!this.config || !this.handle) {
      return;
    }

    this.bindEvents();
    this.initUnavailableModal();
    this.bindSizeSelectorDocumentClose();
    this.initMobileUX();
    this.updateStepsNavMetrics();
    this.hydrateSlots();
  },

  initMobileUX: function () {
    this.initMobileSheet();
    this.initStepsNav();
    this.initSlotNavigation();
    this.initSlotAccordions();
  },

  initMobileSheet: function () {
    const toggle = this.root.querySelector('[data-bundle-mobile-sheet-toggle]');
    const closeButton = this.root.querySelector('[data-bundle-mobile-sheet-close]');
    const sheet = this.root.querySelector('[data-bundle-mobile-sheet]');

    if (!toggle || !sheet) {
      return;
    }

    toggle.addEventListener('click', () => {
      if (this.mobileSheetOpen) {
        this.closeMobileSheet();
      } else {
        this.openMobileSheet();
      }
    });

    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeMobileSheet();
      });
    }
  },

  openMobileSheet: function () {
    const sheet = this.root.querySelector('[data-bundle-mobile-sheet]');
    const toggle = this.root.querySelector('[data-bundle-mobile-sheet-toggle]');

    if (!sheet) {
      return;
    }

    sheet.hidden = false;
    this.mobileSheetOpen = true;
    this.root.classList.add('is-mobile-sheet-open');

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
  },

  closeMobileSheet: function () {
    const sheet = this.root.querySelector('[data-bundle-mobile-sheet]');
    const toggle = this.root.querySelector('[data-bundle-mobile-sheet-toggle]');

    if (!sheet) {
      return;
    }

    sheet.hidden = true;
    this.mobileSheetOpen = false;
    this.root.classList.remove('is-mobile-sheet-open');

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
  },

  initStepsNav: function () {
    const stepsNav = this.root.querySelector('[data-bundle-steps]');

    if (!stepsNav || !this.config || !Array.isArray(this.config.slots)) {
      this.updateStepsNavMetrics();
      return;
    }

    if (this.config.slots.length <= 1) {
      stepsNav.hidden = true;
      this.updateStepsNavMetrics();
      return;
    }

    const stepButtons = stepsNav.querySelectorAll('[data-bundle-step]');
    stepButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = parseInt(button.dataset.stepIndex, 10);

        if (Number.isFinite(index)) {
          this.goToSlot(index);
        }
      });
    });

    this.setupSlotObserver();
    this.setActiveStep(0);
    this.updateMobileSteps();
    this.updateStepsNavMetrics();

    if (!this.stepsNavResizeBound) {
      this.stepsNavResizeBound = true;

      window.addEventListener('resize', () => {
        this.updateStepsNavMetrics();
      });
    }
  },

  updateStepsNavMetrics: function () {
    const stepsNav = this.root.querySelector('[data-bundle-steps]');
    let height = 0;

    if (stepsNav && !stepsNav.hidden) {
      height = stepsNav.offsetHeight;
    }

    this.root.style.setProperty('--bundle-steps-height', `${height}px`);
  },

  initSlotNavigation: function () {
    const slotSections = this.root.querySelectorAll('.bundle-slot');
    const navElements = this.root.querySelectorAll('[data-slot-mobile-nav]');

    if (slotSections.length <= 1) {
      navElements.forEach((nav) => {
        nav.hidden = true;
      });
      return;
    }

    slotSections.forEach((slotSection, index) => {
      const nav = slotSection.querySelector('[data-slot-mobile-nav]');

      if (!nav) {
        return;
      }

      const prevButton = nav.querySelector('[data-slot-prev]');
      const nextButton = nav.querySelector('[data-slot-next]');

      if (prevButton) {
        prevButton.disabled = index === 0;
        prevButton.addEventListener('click', () => {
          this.goToSlot(index - 1);
        });
      }

      if (nextButton) {
        nextButton.disabled = index === slotSections.length - 1;
        nextButton.addEventListener('click', () => {
          this.goToSlot(index + 1);
        });
      }
    });
  },

  initSlotAccordions: function () {
    const toggles = this.root.querySelectorAll('[data-slot-toggle]');

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        if (toggle.disabled) {
          return;
        }

        const slotSection = toggle.closest('.bundle-slot');

        if (!slotSection) {
          return;
        }

        const slotId = slotSection.dataset.slotId;

        if (!slotId) {
          return;
        }

        const isCollapsed = slotSection.classList.contains('is-collapsed');

        if (isCollapsed) {
          slotSection.classList.remove('is-collapsed');
          toggle.setAttribute('aria-expanded', 'true');
          this.slotExpanded.add(slotId);
        } else {
          slotSection.classList.add('is-collapsed');
          toggle.setAttribute('aria-expanded', 'false');
          this.slotExpanded.delete(slotId);
        }

        this.updateSlotAccordionIcons(slotSection);
      });
    });
  },

  updateSlotAccordionIcons: function (slotSection) {
    const iconDown = slotSection.querySelector('[data-slot-icon-down]');
    const iconUp = slotSection.querySelector('[data-slot-icon-up]');
    const isCollapsed = slotSection.classList.contains('is-collapsed');

    if (iconDown) {
      iconDown.classList.toggle('is-visible', isCollapsed);
    }

    if (iconUp) {
      iconUp.classList.toggle('is-visible', !isCollapsed);
    }
  },

  updateSlotAccordions: function () {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return;
    }

    this.config.slots.forEach((slot) => {
      const slotSection = this.root.querySelector(`[data-slot-id="${slot.id}"]`);

      if (!slotSection) {
        return;
      }

      const toggle = slotSection.querySelector('[data-slot-toggle]');
      const iconsWrap = slotSection.querySelector('[data-slot-toggle-icons]');
      const helperEl = slotSection.querySelector('.bundle-slot-helper');
      const previewEl = slotSection.querySelector('[data-slot-selection-preview]');
      const selections = this.selections.get(slot.id) || [];
      const isComplete = this.isSlotComplete(slot);

      if (!toggle) {
        return;
      }

      if (isComplete) {
        toggle.disabled = false;
        slotSection.classList.add('is-slot-complete');

        if (iconsWrap) {
          iconsWrap.hidden = false;
        }

        if (previewEl && selections.length) {
          const names = selections.map((item) => {
            return item.displayName;
          }).join(', ');
          previewEl.textContent = names;
          previewEl.hidden = false;
        }

        if (helperEl) {
          helperEl.hidden = true;
        }

        if (this.slotExpanded.has(slot.id)) {
          slotSection.classList.remove('is-collapsed');
          toggle.setAttribute('aria-expanded', 'true');
        } else {
          slotSection.classList.add('is-collapsed');
          toggle.setAttribute('aria-expanded', 'false');
        }

        this.updateSlotAccordionIcons(slotSection);
        return;
      }

      toggle.disabled = true;
      slotSection.classList.remove('is-slot-complete', 'is-collapsed');
      toggle.setAttribute('aria-expanded', 'true');
      this.slotExpanded.delete(slot.id);

      if (iconsWrap) {
        iconsWrap.hidden = true;
      }

      if (previewEl) {
        previewEl.hidden = true;
        previewEl.textContent = '';
      }

      if (helperEl) {
        helperEl.hidden = false;
      }
    });
  },

  slotNeedsSelection: function (slot) {
    if (slot.required) {
      return true;
    }

    if (slot.min != null && slot.min > 0) {
      return true;
    }

    return false;
  },

  getIncompleteSlots: function () {
    const incomplete = [];

    if (!this.config || !Array.isArray(this.config.slots)) {
      return incomplete;
    }

    this.config.slots.forEach((slot) => {
      if (this.isSlotComplete(slot)) {
        return;
      }

      if (!this.slotNeedsSelection(slot)) {
        return;
      }

      incomplete.push(slot);
    });

    return incomplete;
  },

  getFirstIncompleteRequiredSlotIndex: function (beforeIndex) {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return -1;
    }

    const limit = Math.min(beforeIndex, this.config.slots.length);

    for (let index = 0; index < limit; index += 1) {
      const slot = this.config.slots[index];

      if (!this.isSlotComplete(slot) && this.slotNeedsSelection(slot)) {
        return index;
      }
    }

    return -1;
  },

  getSlotStepNumber: function (slot) {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return 1;
    }

    const slotIndex = this.config.slots.findIndex((configSlot) => {
      return configSlot.id === slot.id;
    });

    if (slotIndex >= 0) {
      return slotIndex + 1;
    }

    if (slot.position != null) {
      return slot.position;
    }

    return 1;
  },

  formatIncompleteSlotsMessage: function (incompleteSlots) {
    if (!incompleteSlots.length) {
      return '';
    }

    const stepNumbers = incompleteSlots.map((slot) => {
      return this.getSlotStepNumber(slot);
    }).sort((first, second) => {
      return first - second;
    });

    if (stepNumbers.length === 1) {
      return `Complete step ${stepNumbers[0]} to add this bundle to cart.`;
    }

    if (stepNumbers.length === 2) {
      return `Complete steps ${stepNumbers[0]} and ${stepNumbers[1]} to add this bundle to cart.`;
    }

    const lastStep = stepNumbers[stepNumbers.length - 1];
    const leadingSteps = stepNumbers.slice(0, -1).join(', ');

    return `Complete steps ${leadingSteps}, and ${lastStep} to add this bundle to cart.`;
  },

  updateIncompleteMessage: function () {
    const incompleteEls = this.root.querySelectorAll('[data-bundle-incomplete]');
    const incompleteSlots = this.getIncompleteSlots();
    const message = this.formatIncompleteSlotsMessage(incompleteSlots);
    const hasStartedSelecting = this.getTotalQuantity() > 0;

    incompleteEls.forEach((incompleteEl) => {
      if (message && !this.isValid() && hasStartedSelecting && !this.bundleUnavailable) {
        incompleteEl.textContent = message;
        incompleteEl.hidden = false;
      } else {
        incompleteEl.textContent = '';
        incompleteEl.hidden = true;
      }
    });
  },

  maybeAdvanceToNextSlot: function (completedSlotId, wasComplete) {
    const slotConfig = this.getSlotConfig(completedSlotId);

    if (!slotConfig || wasComplete || !this.isSlotComplete(slotConfig)) {
      return;
    }

    if (!this.config || !Array.isArray(this.config.slots)) {
      return;
    }

    const slotIndex = this.config.slots.findIndex((slot) => {
      return slot.id === completedSlotId;
    });

    if (slotIndex < 0) {
      return;
    }

    for (let index = slotIndex + 1; index < this.config.slots.length; index += 1) {
      const nextSlot = this.config.slots[index];

      if (!this.isSlotComplete(nextSlot)) {
        this.goToSlot(index);
        return;
      }
    }
  },

  finishSelection: function (slotId, wasComplete) {
    const slotConfig = this.getSlotConfig(slotId);
    const shouldAutoAdvance = Boolean(
      slotConfig && !wasComplete && this.isSlotComplete(slotConfig)
    );

    if (shouldAutoAdvance) {
      this.root.classList.add('is-auto-advancing');
    }

    this.updateSummary();

    if (shouldAutoAdvance) {
      this.maybeAdvanceToNextSlot(slotId, wasComplete);

      window.requestAnimationFrame(() => {
        this.root.classList.remove('is-auto-advancing');
      });
    }

    this.maybeRevealSummary(slotId, wasComplete);
  },

  isMobileLayout: function () {
    return window.matchMedia('(max-width: 979px)').matches;
  },

  maybeRevealSummary: function (slotId, wasComplete) {
    const slotConfig = this.getSlotConfig(slotId);

    if (!slotConfig || wasComplete || !this.isSlotComplete(slotConfig)) {
      return;
    }

    if (this.getIncompleteSlots().length > 0) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.revealSummary();
    });
  },

  revealSummary: function () {
    if (this.isMobileLayout()) {
      const header = this.root.querySelector('.bundle-builder-header');

      if (header) {
        header.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      this.openMobileSheet();
      return;
    }

    const summary = this.root.querySelector('.bundle-builder-summary');

    if (!summary) {
      return;
    }

    summary.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  },

  goToSlot: function (index) {
    const slotSections = this.root.querySelectorAll('.bundle-slot');

    if (index < 0 || index >= slotSections.length) {
      return;
    }

    if (slotSections[index].hidden) {
      let visibleIndex = -1;

      for (let forwardIndex = index + 1; forwardIndex < slotSections.length; forwardIndex += 1) {
        if (!slotSections[forwardIndex].hidden) {
          visibleIndex = forwardIndex;
          break;
        }
      }

      if (visibleIndex < 0) {
        for (let backwardIndex = index - 1; backwardIndex >= 0; backwardIndex -= 1) {
          if (!slotSections[backwardIndex].hidden) {
            visibleIndex = backwardIndex;
            break;
          }
        }
      }

      if (visibleIndex < 0) {
        return;
      }

      index = visibleIndex;
    }

    const blockingIndex = this.getFirstIncompleteRequiredSlotIndex(index);

    if (blockingIndex >= 0) {
      index = blockingIndex;
    }

    const slotSection = slotSections[index];
    const slotId = slotSection.dataset.slotId;

    if (slotId && slotSection.classList.contains('is-slot-complete')) {
      slotSection.classList.remove('is-collapsed');
      this.slotExpanded.add(slotId);

      const toggle = slotSection.querySelector('[data-slot-toggle]');

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'true');
      }

      this.updateSlotAccordionIcons(slotSection);
    }

    this.activeSlotIndex = index;
    this.setActiveStep(index);
    this.scrollActiveStepIntoView(index);
    this.closeMobileSheet();
    this.scrollToSlotElement(slotSection);
  },

  getScrollOffset: function () {
    const heightValue = window.getComputedStyle(this.root).getPropertyValue('--bundle-steps-height').trim();
    const stepsHeight = parseFloat(heightValue) || 0;

    return stepsHeight + 16;
  },

  scrollToSlotElement: function (slotSection) {
    const scrollTarget = slotSection.querySelector('.bundle-slot-header') || slotSection;
    const offset = this.getScrollOffset();
    const top = scrollTarget.getBoundingClientRect().top + window.scrollY - offset;

    this.isProgrammaticScroll = true;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });

    window.setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 800);
  },

  setupSlotObserver: function () {
    const slotSections = this.root.querySelectorAll('.bundle-slot');

    if (!slotSections.length) {
      return;
    }

    if (this.slotObserver) {
      this.slotObserver.disconnect();
    }

    const stepsNav = this.root.querySelector('[data-bundle-steps]');
    let rootMargin = '-40% 0px -40% 0px';

    if (stepsNav && !stepsNav.hidden) {
      const navHeight = stepsNav.offsetHeight;
      rootMargin = `-${navHeight + 8}px 0px -50% 0px`;
    }

    this.slotObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.isProgrammaticScroll) {
            return;
          }

          const slotSection = entry.target;
          const allSlotSections = this.root.querySelectorAll('.bundle-slot');
          let index = 0;

          for (let sectionIndex = 0; sectionIndex < allSlotSections.length; sectionIndex += 1) {
            if (allSlotSections[sectionIndex] === slotSection) {
              index = sectionIndex;
              break;
            }
          }

          this.activeSlotIndex = index;
          this.setActiveStep(index);
        });
      },
      {
        root: null,
        rootMargin: rootMargin,
        threshold: 0,
      }
    );

    slotSections.forEach((slotSection) => {
      this.slotObserver.observe(slotSection);
    });
  },

  setActiveStep: function (index) {
    const stepButtons = this.root.querySelectorAll('[data-bundle-step]');
    stepButtons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === index;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'step' : 'false');
    });
  },

  scrollActiveStepIntoView: function (index) {
    if (!this.isMobileLayout()) {
      return;
    }

    const stepsNav = this.root.querySelector('[data-bundle-steps]');

    if (!stepsNav || stepsNav.hidden) {
      return;
    }

    const stepButtons = stepsNav.querySelectorAll('[data-bundle-step]');
    const button = stepButtons[index];

    if (!button) {
      return;
    }

    window.requestAnimationFrame(() => {
      const navRect = stepsNav.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const targetScrollLeft = stepsNav.scrollLeft + (buttonRect.left - navRect.left);

      stepsNav.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth',
      });
    });
  },

  isSlotComplete: function (slot) {
    const slotSelections = this.selections.get(slot.id) || [];
    const slotQty = slotSelections.reduce((sum, item) => {
      return sum + item.quantity;
    }, 0);

    if (slot.required && slotQty === 0) {
      return false;
    }

    if (slot.min != null && slotQty < slot.min) {
      return false;
    }

    if (slot.max != null && slotQty > slot.max) {
      return false;
    }

    return true;
  },

  updateStepCheck: function (button, isComplete) {
    const checkEl = button.querySelector('[data-step-check]');

    if (isComplete) {
      if (checkEl) {
        return;
      }

      const template = this.root.querySelector('[data-bundle-step-check-template]');

      if (!template || !template.content || !template.content.firstElementChild) {
        return;
      }

      button.appendChild(template.content.firstElementChild.cloneNode(true));
      return;
    }

    if (checkEl) {
      checkEl.remove();
    }
  },

  updateMobileSteps: function () {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return;
    }

    const stepsNav = this.root.querySelector('[data-bundle-steps]');
    const allRequiredComplete = this.getIncompleteSlots().length === 0;

    if (stepsNav) {
      stepsNav.classList.toggle('is-all-complete', allRequiredComplete);
    }

    this.config.slots.forEach((slot) => {
      const button = this.root.querySelector(`[data-bundle-step="${slot.id}"]`);

      if (!button) {
        return;
      }

      const isComplete = this.isSlotComplete(slot);
      const needsSelection = this.slotNeedsSelection(slot);
      button.classList.toggle('is-complete', isComplete);
      button.classList.toggle('is-incomplete', !isComplete && needsSelection);

      this.updateStepCheck(button, isComplete);
    });

    const mobileCountEl = this.root.querySelector('[data-bundle-mobile-count]');

    if (mobileCountEl) {
      const totalQty = this.getTotalQuantity();
      mobileCountEl.textContent = `${totalQty} item${totalQty === 1 ? '' : 's'}`;
    }
  },

  bindEvents: function () {
    const addButtons = this.root.querySelectorAll('[data-bundle-add], [data-bundle-add-mobile]');

    addButtons.forEach((button) => {
      this.addToCartLabels.set(button, button.textContent.trim());
      button.addEventListener('click', () => {
        this.addToCart();
      });
    });
  },

  initUnavailableModal: function () {
    const modal = this.root.querySelector('[data-bundle-unavailable]');

    if (!modal || this.unavailableModalBound) {
      return;
    }

    this.unavailableModalBound = true;

    const closeTriggers = modal.querySelectorAll('[data-bundle-unavailable-close]');

    closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.dismissUnavailableModal();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (!this.isUnavailableModalOpen()) {
        return;
      }

      event.preventDefault();
      this.dismissUnavailableModal();
    });
  },

  isUnavailableModalOpen: function () {
    const modal = this.root.querySelector('[data-bundle-unavailable]');

    if (!modal) {
      return false;
    }

    return !modal.hidden;
  },

  showUnavailableModal: function () {
    if (this.unavailableModalDismissed) {
      return;
    }

    const modal = this.root.querySelector('[data-bundle-unavailable]');

    if (!modal) {
      return;
    }

    const dialog = modal.querySelector('.bundle-unavailable-modal-dialog');
    const primaryAction = modal.querySelector('.bundle-unavailable-modal-action');

    this.unavailableModalFocusReturn = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    this.root.classList.add('is-unavailable-modal-open');
    document.body.classList.add('bundle-unavailable-modal-open');

    window.requestAnimationFrame(() => {
      if (primaryAction instanceof HTMLElement) {
        primaryAction.focus();
        return;
      }

      if (dialog instanceof HTMLElement) {
        dialog.focus();
      }
    });
  },

  dismissUnavailableModal: function () {
    const modal = this.root.querySelector('[data-bundle-unavailable]');

    if (!modal) {
      return;
    }

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    this.unavailableModalDismissed = true;
    this.root.classList.remove('is-unavailable-modal-open');
    document.body.classList.remove('bundle-unavailable-modal-open');

    if (this.unavailableModalFocusReturn instanceof HTMLElement) {
      this.unavailableModalFocusReturn.focus();
      this.unavailableModalFocusReturn = null;
    }
  },

  hideUnavailableModal: function () {
    const modal = this.root.querySelector('[data-bundle-unavailable]');

    if (!modal) {
      return;
    }

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    this.root.classList.remove('is-unavailable-modal-open');
    document.body.classList.remove('bundle-unavailable-modal-open');
    this.unavailableModalDismissed = false;
    this.unavailableModalFocusReturn = null;
  },

  hydrateSlots: function () {
    const itemsUrl = `/apps/bundle/${encodeURIComponent(this.handle)}/items`;

    fetch(itemsUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = `Items request failed (${response.status})`;
          const contentType = response.headers.get('content-type') || '';

          if (contentType.includes('application/json')) {
            try {
              const errorBody = await response.json();

              if (errorBody && typeof errorBody.error === 'string') {
                errorMessage = errorBody.error;
              }
            } catch (parseError) {
              console.warn('BundlePage: could not parse items error response', parseError);
            }
          }

          throw new Error(errorMessage);
        }

        return response.json();
      })
      .then((data) => {
        if (!data || !Array.isArray(data.slots)) {
          throw new Error('Items response missing slots');
        }

        // Bundle app returns shop/catalog (often USD) prices; overlay market
        // presentment prices from the Ajax product API before rendering.
        return this.applyPresentmentPrices(data.slots).then((slots) => {
          return { ...data, slots };
        });
      })
      .then((data) => {
        data.slots.forEach((slotData) => {
          const products = (slotData.products || []).map((product) => {
            return this.normalizeSlotProduct(product);
          });

          this.slotProducts.set(slotData.slotId, products);
          this.renderSlotProducts(slotData.slotId, products);
          this.updateSlotHint(slotData.slotId);
        });

        this.handleUnavailableSlots();
        this.updateSummary();
      })
      .catch((error) => {
        console.warn('BundlePage: could not hydrate slot items', error);
        this.showGlobalError('Unable to load bundle products. Please refresh and try again.');
        this.clearSlotSkeletons();
      });
  },

  /**
   * The /apps/bundle/.../items endpoint returns prices in the app/catalog
   * currency (typically USD). /products/{handle}.js returns the visitor's
   * market presentment prices — use those for display and totals.
   */
  applyPresentmentPrices: function (slots) {
    const handles = new Set();

    (slots || []).forEach((slot) => {
      (slot.products || []).forEach((product) => {
        if (product && product.handle) {
          handles.add(product.handle);
        }
      });
    });

    if (!handles.size) {
      return Promise.resolve(slots);
    }

    return Promise.all(
      Array.from(handles).map((handle) => {
        return fetch(`/products/${encodeURIComponent(handle)}.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) {
              return null;
            }

            return response.json();
          })
          .then((productJson) => {
            if (!productJson) {
              return [handle, null];
            }

            const variantPrices = {};
            (productJson.variants || []).forEach((variant) => {
              const id = String(variant.id);
              variantPrices[id] = this.centsToDecimalString(variant.price);
            });

            return [
              handle,
              {
                price: this.centsToDecimalString(productJson.price),
                variants: variantPrices,
              },
            ];
          })
          .catch((error) => {
            console.warn('BundlePage: could not load presentment price for', handle, error);
            return [handle, null];
          });
      })
    ).then((entries) => {
      const priceByHandle = {};
      entries.forEach(([handle, prices]) => {
        if (prices) {
          priceByHandle[handle] = prices;
        }
      });

      (slots || []).forEach((slot) => {
        (slot.products || []).forEach((product) => {
          const prices = priceByHandle[product.handle];
          if (!prices) {
            return;
          }

          // Shopify's product-level price is its default variant's price, which can be
          // cheaper than this entry's actual variant (e.g. a smaller pack size), so match
          // by variant ID first and only fall back to the product-level price.
          const numericProductVariantId = this.gidToNumericId(product.variantId);
          if (numericProductVariantId && prices.variants[numericProductVariantId] != null) {
            product.price = prices.variants[numericProductVariantId];
          } else {
            product.price = prices.price;
          }

          (product.variants || []).forEach((variant) => {
            const numericId = this.gidToNumericId(variant.id);
            if (numericId && prices.variants[numericId] != null) {
              variant.price = prices.variants[numericId];
            }
          });
        });
      });

      return slots;
    });
  },

  centsToDecimalString: function (cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) {
      return '0.00';
    }

    return (value / 100).toFixed(2);
  },

  clearSlotSkeletons: function () {
    const skeletons = this.root.querySelectorAll('.bundle-slot-skeleton');
    skeletons.forEach((skeleton) => {
      skeleton.remove();
    });
  },

  clearGridSkeletons: function (grid) {
    const skeletons = grid.querySelectorAll('.bundle-slot-skeleton');
    skeletons.forEach((skeleton) => {
      skeleton.remove();
    });
  },

  bindProductImage: function (imageWrap, image, src) {
    const markLoaded = () => {
      imageWrap.classList.add('is-loaded');
    };

    image.decoding = 'async';
    image.addEventListener('load', markLoaded, { once: true });
    image.addEventListener('error', markLoaded, { once: true });

    if (src) {
      image.src = src;
    }

    if (image.complete && image.naturalWidth > 0) {
      markLoaded();
    }
  },

  bindSizeSelectorDocumentClose: function () {
    if (this.sizeSelectorDocumentBound) {
      return;
    }

    this.sizeSelectorDocumentBound = true;

    document.addEventListener('click', (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest('.lp-size-wrapper')) {
        return;
      }

      document.querySelectorAll('.lp-size-wrapper.active').forEach((wrapper) => {
        wrapper.classList.remove('active', 'open-up');

        const trigger = wrapper.querySelector('.lp-size-trigger');

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  },

  getOptionByName: function (product, names) {
    if (!product || !Array.isArray(product.options)) {
      return null;
    }

    const normalizedNames = names.map((name) => {
      return name.toLowerCase();
    });

    return product.options.find((option) => {
      return normalizedNames.includes(option.name.toLowerCase());
    }) || null;
  },

  getColorMetaForValue: function (product, colorValue) {
    if (!product || !Array.isArray(product.colorOptions) || !colorValue) {
      return null;
    }

    const normalizedValue = this.normalizeColorLabel(colorValue);

    return product.colorOptions.find((entry) => {
      return this.normalizeColorLabel(entry.displayName) === normalizedValue;
    }) || null;
  },

  normalizeColorLabel: function (value) {
    return (value ?? '').trim().toLowerCase();
  },

  normalizeSlotProduct: function (product) {
    if (!product || product.hasOptions || !product.variantId) {
      return product;
    }

    if (Array.isArray(product.variants) && product.variants.length) {
      return product;
    }

    const variant = {
      id: product.variantId,
      available: product.available,
    };

    if (product.shape) {
      variant.shape = product.shape;
    }

    const normalizedProduct = { ...product };
    delete normalizedProduct.shape;
    normalizedProduct.variants = [variant];

    return normalizedProduct;
  },

  isProductPurchasable: function (product) {
    if (!product) {
      return false;
    }

    if (Array.isArray(product.variants) && product.variants.length) {
      const hasAvailableVariant = product.variants.some((variant) => {
        return variant.available === true;
      });

      if (hasAvailableVariant) {
        return true;
      }

      const allVariantsSoldOut = product.variants.every((variant) => {
        return variant.available === false;
      });

      if (allVariantsSoldOut) {
        return false;
      }
    }

    return product.available === true;
  },

  isSlotFullySoldOut: function (slotId) {
    const products = this.slotProducts.get(slotId) || [];

    if (!products.length) {
      return false;
    }

    return products.every((product) => {
      return !this.isProductPurchasable(product);
    });
  },

  getVariantShape: function (variant) {
    return (variant?.shape ?? '').trim();
  },

  getCardVariant: function (product) {
    return this.getDefaultDisplayVariant(product);
  },

  getDefaultDisplayVariant: function (product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) {
      return null;
    }

    if (product.variantId) {
      const match = product.variants.find((variant) => {
        return variant.id === product.variantId;
      });

      if (match) {
        return match;
      }
    }

    const featuredUrl = product.featuredImage?.url;

    if (featuredUrl) {
      const imageMatch = product.variants.find((variant) => {
        return variant.image?.url === featuredUrl;
      });

      if (imageMatch) {
        return imageMatch;
      }
    }

    const availableMatch = product.variants.find((variant) => {
      return variant.available;
    });

    return availableMatch || product.variants[0];
  },

  getDefaultDisplayColor: function (product) {
    const variant = this.getDefaultDisplayVariant(product);

    if (!variant) {
      return null;
    }

    return this.getVariantOptionValues(variant, product).color || null;
  },

  setProductCardShapeBadge: function (imageWrap, variant) {
    if (!imageWrap) {
      return;
    }

    const normalizedShape = this.getVariantShape(variant);
    let badge = imageWrap.querySelector('.bundle-product-card-shape-badge');

    if (!normalizedShape) {
      if (badge) {
        badge.remove();
      }

      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'bundle-product-card-shape-badge';
      imageWrap.appendChild(badge);
    }

    badge.textContent = normalizedShape;
  },

  getVariantOptionValues: function (variant, product) {
    const result = {
      color: null,
      size: null,
    };

    if (!variant) {
      return result;
    }

    if (Array.isArray(product.options) && product.options.length) {
      product.options.forEach((option) => {
        const optionKey = option.position === 0 ? 'option1' : option.position === 1 ? 'option2' : 'option3';
        const value = variant[optionKey];
        const name = option.name.toLowerCase();

        if (name === 'size') {
          result.size = value;
        }

        if (name === 'color' || name === 'colour') {
          result.color = value;
        }
      });

      return result;
    }

    result.color = variant.option1 || null;
    result.size = variant.option2 || null;

    return result;
  },

  findVariantForOptions: function (product, color, size) {
    if (!product || !Array.isArray(product.variants)) {
      return null;
    }

    return product.variants.find((variant) => {
      const optionValues = this.getVariantOptionValues(variant, product);

      if (size && optionValues.size !== size) {
        return false;
      }

      if (color && optionValues.color !== color) {
        return false;
      }

      if (size) {
        return true;
      }

      if (color && !size) {
        return optionValues.color === color;
      }

      return Boolean(color);
    }) || null;
  },

  getResolvedVariant: function (product, state) {
    if (!product || !state) {
      return null;
    }

    const sizeOption = this.getOptionByName(product, ['Size']);
    const colorOption = this.getOptionByName(product, ['Color', 'Colour']);

    if (sizeOption && !state.selectedSize) {
      return null;
    }

    if (colorOption && colorOption.values.length > 1 && !state.selectedColor) {
      return null;
    }

    return this.findVariantForOptions(product, state.selectedColor, state.selectedSize);
  },

  buildSelectionDisplayName: function (product, variant) {
    if (variant.displayName) {
      return variant.displayName;
    }

    if (variant.title && variant.title !== 'Default Title') {
      return `${product.title} — ${variant.title}`;
    }

    return product.title || product.displayName || '';
  },

  buildResolvedProduct: function (card, product) {
    const state = card.bundleOptionState;
    const variant = this.getResolvedVariant(product, state);

    if (!variant) {
      return null;
    }

    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      variantId: variant.id,
      price: variant.price,
      available: variant.available,
      displayName: this.buildSelectionDisplayName(product, variant),
      featuredImage: variant.image || product.featuredImage || null,
      hasOptions: true,
      options: product.options,
      variants: product.variants,
    };
  },

  isSizeOnlyOptionRemaining: function (product, state) {
    const sizeOption = this.getOptionByName(product, ['Size']);
    const colorOption = this.getOptionByName(product, ['Color', 'Colour']);

    if (!sizeOption || !state) {
      return false;
    }

    if (colorOption && colorOption.values.length > 1 && !state.selectedColor) {
      return false;
    }

    return true;
  },

  attemptAddProductFromCard: function (slotId, card, product) {
    const selectionProduct = this.buildResolvedProduct(card, product);

    if (!selectionProduct) {
      this.showSlotError(slotId, 'Select all options before adding this item.');
      return;
    }

    if (!selectionProduct.available) {
      this.showSlotError(slotId, 'That size is sold out. Choose another size.');
      return;
    }

    this.toggleSelection(slotId, selectionProduct, card);
  },

  getSizeValuesForColor: function (product, colorValue) {
    const sizeOption = this.getOptionByName(product, ['Size']);

    if (!sizeOption) {
      return [];
    }

    const colorOption = this.getOptionByName(product, ['Color', 'Colour']);
    let variants = product.variants || [];

    if (colorValue && colorOption) {
      variants = variants.filter((variant) => {
        const optionValues = this.getVariantOptionValues(variant, product);
        return optionValues.color === colorValue;
      });
    }

    return sizeOption.values.filter((sizeValue) => {
      return variants.some((variant) => {
        const optionValues = this.getVariantOptionValues(variant, product);
        return optionValues.size === sizeValue;
      });
    });
  },

  findCardByVariantId: function (grid, variantId) {
    const directMatch = grid.querySelector(`.bundle-product-card[data-variant-id="${variantId}"]`);

    if (directMatch) {
      return directMatch;
    }

    const optionCards = grid.querySelectorAll('.bundle-product-card[data-has-options="true"]');

    for (let index = 0; index < optionCards.length; index += 1) {
      const card = optionCards[index];
      const product = card.bundleProduct;

      if (!product || !Array.isArray(product.variants)) {
        continue;
      }

      const hasVariant = product.variants.some((variant) => {
        return variant.id === variantId;
      });

      if (hasVariant) {
        return card;
      }
    }

    return null;
  },

  countProductVariantsInSlot: function (slotId, product) {
    const current = this.selections.get(slotId) || [];

    if (!product || !Array.isArray(product.variants)) {
      return 0;
    }

    const variantIds = new Set(product.variants.map((variant) => {
      return variant.id;
    }));

    return current.filter((item) => {
      return variantIds.has(item.variantId);
    }).length;
  },

  clearProductSelection: function (slotId, product) {
    const current = this.selections.get(slotId) || [];
    const variantIds = new Set((product.variants || []).map((variant) => {
      return variant.id;
    }));
    const next = current.filter((item) => {
      return !variantIds.has(item.variantId);
    });

    if (next.length === current.length) {
      return;
    }

    this.selections.set(slotId, next);
    this.syncSlotCardStates(slotId);
    this.updateSummary();
  },

  updateProductOptionUI: function (card, product) {
    const state = card.bundleOptionState;

    if (!state) {
      return;
    }

    const colorOption = this.getOptionByName(product, ['Color', 'Colour']);
    const sizeItems = card.querySelectorAll('.lp-size-item');
    const valueText = card.querySelector('.lp-size-value');
    const labelText = card.querySelector('.lp-size-label');
    const priceEl = card.querySelector('.bundle-product-card-price');
    const resolvedVariant = this.getResolvedVariant(product, state);
    const colorValue = state.selectedColor;

    sizeItems.forEach((item) => {
      const sizeValue = item.dataset.value;
      const variantMatch = this.findVariantForOptions(product, colorValue, sizeValue);
      const isSelected = state.selectedSize === sizeValue;

      item.classList.toggle('selected', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');

      if (!variantMatch || !variantMatch.available) {
        item.classList.add('disabled');
      } else {
        item.classList.remove('disabled');
      }
    });

    if (valueText) {
      valueText.textContent = state.selectedSize || 'Select Size';
    }

    if (labelText) {
      labelText.style.display = state.selectedSize ? 'inline' : 'none';
    }

    if (colorOption && colorOption.values.length > 1) {
      const swatches = card.querySelectorAll('.swatch-btn-color');

      swatches.forEach((swatch) => {
        const isSelected = swatch.dataset.swatchValue === colorValue;
        swatch.classList.toggle('selected', isSelected);
        swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    }

    if (resolvedVariant) {
      card.dataset.variantId = resolvedVariant.id;
      card.dataset.price = resolvedVariant.price || '0';
      card.dataset.displayName = this.buildSelectionDisplayName(product, resolvedVariant);
      card.classList.add('is-options-ready');

      if (priceEl) {
        priceEl.textContent = this.formatDecimalPrice(resolvedVariant.price);
      }

      const image = card.querySelector('.bundle-product-card-image img');

      if (image && resolvedVariant.image && resolvedVariant.image.url) {
        const imageWrap = card.querySelector('.bundle-product-card-image');
        this.bindProductImage(imageWrap, image, resolvedVariant.image.url);
      }

      const imageWrap = card.querySelector('.bundle-product-card-image');
      this.setProductCardShapeBadge(imageWrap, resolvedVariant);
    } else {
      card.dataset.variantId = '';
      card.dataset.displayName = '';
      card.classList.remove('is-options-ready');

      if (priceEl) {
        priceEl.textContent = this.formatDecimalPrice(product.price);
      }

      const imageWrap = card.querySelector('.bundle-product-card-image');
      this.setProductCardShapeBadge(imageWrap, null);
    }
  },

  bindSizeSelectorToggle: function (wrapper) {
    const trigger = wrapper.querySelector('.lp-size-trigger');

    if (!trigger) {
      return;
    }

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const isActive = wrapper.classList.contains('active');

      document.querySelectorAll('.lp-size-wrapper.active').forEach((activeWrapper) => {
        activeWrapper.classList.remove('active', 'open-up');

        const activeTrigger = activeWrapper.querySelector('.lp-size-trigger');

        if (activeTrigger) {
          activeTrigger.setAttribute('aria-expanded', 'false');
        }
      });

      if (isActive) {
        wrapper.classList.remove('active', 'open-up');
        trigger.setAttribute('aria-expanded', 'false');
        return;
      }

      wrapper.classList.add('active');
      trigger.setAttribute('aria-expanded', 'true');

      const rect = wrapper.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow < 220) {
        wrapper.classList.add('open-up');
      } else {
        wrapper.classList.remove('open-up');
      }
    });
  },

  createBundleColorSwatches: function (product, slotId, card, state) {
    const template = this.root.querySelector('[data-bundle-color-swatch-template]');

    if (!template || !template.content || !template.content.firstElementChild) {
      return null;
    }

    const colorOption = this.getOptionByName(product, ['Color', 'Colour']);

    if (!colorOption || colorOption.values.length <= 1) {
      return null;
    }

    const wrapper = template.content.firstElementChild.cloneNode(true);
    const optionsContainer = wrapper.querySelector('.swatch-options');

    if (!optionsContainer) {
      return null;
    }

    colorOption.values.forEach((colorValue) => {
      const variantMatch = this.findVariantForOptions(product, colorValue, null);
      const colorMeta = this.getColorMetaForValue(product, colorValue);
      const bgColor = colorMeta?.bgColor || '';
      const bgColor2 = colorMeta?.bgColor2 || '';
      const swatchImage = variantMatch?.image?.url ? variantMatch.image : null;

      if (!swatchImage && !bgColor && !bgColor2) {
        return;
      }

      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'swatch-btn swatch-btn-color';
      swatch.dataset.optionName = 'color';
      swatch.dataset.swatchValue = colorValue;
      swatch.setAttribute('aria-label', `Select color: ${colorValue}`);
      swatch.setAttribute('aria-pressed', 'false');

      if (bgColor2) {
        swatch.style.background = `linear-gradient(-45deg, ${bgColor} 50%, ${bgColor2} 50%)`;
      } else if (bgColor) {
        swatch.style.backgroundColor = bgColor;
      }

      if (bgColor) {
        swatch.dataset.bgColor = bgColor;
      }

      if (swatchImage && !bgColor && !bgColor2) {
        const image = document.createElement('img');
        image.src = swatchImage.url;
        image.alt = swatchImage.altText || colorValue;
        image.className = 'swatch-btn-image';
        swatch.appendChild(image);
      }

      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.clearProductSelection(slotId, product);
        state.selectedColor = colorValue;
        state.selectedSize = null;

        const sizeOption = this.getOptionByName(product, ['Size']);

        if (sizeOption) {
          this.rebuildSizeGrid(card, product, slotId);
        } else {
          this.updateProductOptionUI(card, product);
        }
      });

      optionsContainer.appendChild(swatch);
    });

    return wrapper;
  },

  createBundleSizeSelector: function (product, slotId) {
    const template = this.root.querySelector('[data-bundle-size-selector-template]');

    if (!template || !template.content || !template.content.firstElementChild) {
      return null;
    }

    const uniqueId = `${product.id}-${slotId}`.replace(/\s/g, '-');
    const wrapper = template.content.firstElementChild.cloneNode(true);
    const trigger = wrapper.querySelector('.lp-size-trigger');
    const popup = wrapper.querySelector('.lp-size-popup');

    if (popup) {
      popup.id = `size-list-${uniqueId}`;
    }

    if (trigger) {
      trigger.setAttribute('aria-controls', `size-list-${uniqueId}`);
    }

    this.bindSizeSelectorToggle(wrapper);

    return wrapper;
  },

  rebuildSizeGrid: function (card, product, slotId) {
    const grid = card.querySelector('.lp-size-grid');

    if (!grid) {
      return;
    }

    const state = card.bundleOptionState;
    const sizeValues = this.getSizeValuesForColor(product, state.selectedColor);

    grid.innerHTML = '';

    sizeValues.forEach((sizeValue) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'lp-size-item';
      item.dataset.value = sizeValue;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.textContent = sizeValue;

      item.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (item.classList.contains('disabled')) {
          return;
        }

        if (slotId) {
          this.clearProductSelection(slotId, product);
        }

        state.selectedSize = sizeValue;

        const wrapper = card.querySelector('.lp-size-wrapper');

        if (wrapper) {
          wrapper.classList.remove('active', 'open-up');

          const trigger = wrapper.querySelector('.lp-size-trigger');

          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }

        this.updateProductOptionUI(card, product);

        if (this.isSizeOnlyOptionRemaining(product, state)) {
          this.attemptAddProductFromCard(slotId, card, product);
        }
      });

      grid.appendChild(item);
    });

    this.updateProductOptionUI(card, product);
  },

  initProductOptionSelectors: function (slotId, card, product) {
    const state = {
      selectedColor: null,
      selectedSize: null,
    };
    card.bundleOptionState = state;

    const optionsWrap = document.createElement('div');
    optionsWrap.className = 'bundle-product-card-options';

    const colorOption = this.getOptionByName(product, ['Color', 'Colour']);
    const sizeOption = this.getOptionByName(product, ['Size']);

    if (colorOption && colorOption.values.length === 1) {
      state.selectedColor = colorOption.values[0];
    }

    if (colorOption && colorOption.values.length > 1) {
      const defaultColor = this.getDefaultDisplayColor(product);

      if (defaultColor && colorOption.values.includes(defaultColor)) {
        state.selectedColor = defaultColor;
      }

      const colorSwatches = this.createBundleColorSwatches(product, slotId, card, state);

      if (colorSwatches) {
        optionsWrap.appendChild(colorSwatches);
      }
    }

    if (sizeOption) {
      const wrapper = this.createBundleSizeSelector(product, slotId);

      if (wrapper) {
        optionsWrap.appendChild(wrapper);
      }
    }

    const hintTemplate = this.root.querySelector('[data-bundle-product-card-options-hint-template]');

    if (hintTemplate && hintTemplate.content && hintTemplate.content.firstElementChild) {
      optionsWrap.appendChild(hintTemplate.content.firstElementChild.cloneNode(true));
    }

    card.appendChild(optionsWrap);
    this.rebuildSizeGrid(card, product, slotId);
  },

  renderSlotProducts: function (slotId, products) {
    const grid = this.root.querySelector(`[data-slot-grid="${slotId}"]`);
    if (!grid) {
      return;
    }

    this.clearGridSkeletons(grid);
    grid.innerHTML = '';

    if (!products.length) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'bundle-slot-empty';
      emptyMessage.textContent = 'No products available for this slot.';
      grid.appendChild(emptyMessage);
      return;
    }

    products.forEach((product) => {
      const card = this.createProductCard(slotId, product);
      grid.appendChild(card);
    });

    this.syncSlotCardStates(slotId);
  },

  handleUnavailableSlots: function () {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return;
    }

    let hasUnavailableRequiredSlot = false;

    this.config.slots.forEach((slot) => {
      const slotSection = this.root.querySelector(`[data-slot-id="${slot.id}"]`);
      const grid = this.root.querySelector(`[data-slot-grid="${slot.id}"]`);
      const products = this.slotProducts.get(slot.id) || [];
      const hasRenderedCards = Boolean(grid && grid.querySelector('.bundle-product-card'));
      const isSlotEmpty = !hasRenderedCards && products.length === 0;
      const isSlotFullySoldOut = this.isSlotFullySoldOut(slot.id);

      if (slotSection) {
        slotSection.classList.remove('is-slot-unavailable');
      }

      this.hideSlotError(slot.id);

      if (isSlotEmpty) {
        if (this.slotNeedsSelection(slot)) {
          hasUnavailableRequiredSlot = true;
          this.markRequiredSlotUnavailable(slot.id);
        } else {
          this.hideEmptySlot(slot.id);
        }
        return;
      }

      if (isSlotFullySoldOut && this.slotNeedsSelection(slot)) {
        hasUnavailableRequiredSlot = true;
        this.markRequiredSlotSoldOut(slot.id);
        return;
      }
    });

    this.bundleUnavailable = hasUnavailableRequiredSlot;

    if (hasUnavailableRequiredSlot) {
      this.showUnavailableModal();
    } else {
      this.hideUnavailableModal();
    }
  },

  hideEmptySlot: function (slotId) {
    const slotSection = this.root.querySelector(`[data-slot-id="${slotId}"]`);
    const stepButton = this.root.querySelector(`[data-bundle-step="${slotId}"]`);

    if (slotSection) {
      slotSection.hidden = true;
    }

    if (stepButton) {
      stepButton.hidden = true;
    }
  },

  markRequiredSlotUnavailable: function (slotId) {
    const slotSection = this.root.querySelector(`[data-slot-id="${slotId}"]`);

    if (!slotSection) {
      return;
    }

    slotSection.classList.add('is-slot-unavailable');

    const emptyMessage = slotSection.querySelector('.bundle-slot-empty');

    if (emptyMessage) {
      emptyMessage.textContent = 'These items are sold out and this step is required, so the bundle is currently unavailable.';
    }
  },

  markRequiredSlotSoldOut: function (slotId) {
    const slotSection = this.root.querySelector(`[data-slot-id="${slotId}"]`);

    if (!slotSection) {
      return;
    }

    slotSection.classList.add('is-slot-unavailable');
    this.showSlotError(
      slotId,
      'These items are sold out and this step is required, so the bundle is currently unavailable.'
    );
  },

  createProductCard: function (slotId, product) {
    const showQtyControls = this.slotCanPickDuplicates(slotId);
    const hasOptions = product.hasOptions === true;
    const card = document.createElement('div');
    card.className = 'bundle-product-card';
    card.dataset.slotId = slotId;
    card.dataset.productId = product.id || '';
    card.dataset.price = product.price || '0';
    card.dataset.displayName = product.displayName || product.title || '';
    card.dataset.handle = product.handle || '';
    card.bundleProduct = product;

    if (hasOptions) {
      card.dataset.hasOptions = 'true';
      card.dataset.variantId = '';
    } else {
      card.dataset.variantId = product.variantId || '';
    }

    const selectButton = document.createElement('button');
    selectButton.type = 'button';
    selectButton.className = 'bundle-product-card-main';

    if (!product.available) {
      card.classList.add('is-unavailable');
      selectButton.disabled = true;
    }

    const imageWrap = document.createElement('div');
    imageWrap.className = 'bundle-product-card-image';

    if (product.featuredImage && product.featuredImage.url) {
      const image = document.createElement('img');
      image.alt = product.featuredImage.altText || product.displayName || product.title || '';
      image.loading = 'lazy';
      imageWrap.appendChild(image);
      this.bindProductImage(imageWrap, image, product.featuredImage.url);
    } else {
      imageWrap.classList.add('is-loaded');
      const placeholder = document.createElement('span');
      placeholder.className = 'bundle-product-card-placeholder';
      placeholder.textContent = 'No image';
      imageWrap.appendChild(placeholder);
    }

    const cardVariant = this.getCardVariant(product);
    this.setProductCardShapeBadge(imageWrap, cardVariant);

    const body = document.createElement('div');
    body.className = 'bundle-product-card-body';

    const title = document.createElement('p');
    title.className = 'bundle-product-card-title';
    title.textContent = hasOptions
      ? (product.title || product.displayName || '')
      : (product.displayName || product.title || '');

    const price = document.createElement('p');
    price.className = 'bundle-product-card-price';
    price.textContent = this.formatDecimalPrice(product.price);

    body.appendChild(title);
    body.appendChild(price);

    if (!product.available) {
      const badge = document.createElement('span');
      badge.className = 'bundle-product-card-badge';
      badge.textContent = 'Sold out';
      body.appendChild(badge);
    }

    selectButton.appendChild(imageWrap);
    selectButton.appendChild(body);
    card.appendChild(selectButton);

    if (hasOptions) {
      this.initProductOptionSelectors(slotId, card, product);
    }

    if (showQtyControls) {
      const qtyWrap = document.createElement('div');
      qtyWrap.className = 'bundle-product-card-qty';
      qtyWrap.dataset.cardQty = 'true';
      qtyWrap.hidden = true;

      const decreaseButton = document.createElement('button');
      decreaseButton.type = 'button';
      decreaseButton.className = 'bundle-product-card-qty-button is-decrease';
      decreaseButton.setAttribute('aria-label', `Remove one ${product.title || product.displayName || 'item'}`);
      decreaseButton.textContent = '−';

      const qtyValue = document.createElement('span');
      qtyValue.className = 'bundle-product-card-qty-value';
      qtyValue.dataset.qtyValue = 'true';
      qtyValue.textContent = '0';

      const increaseButton = document.createElement('button');
      increaseButton.type = 'button';
      increaseButton.className = 'bundle-product-card-qty-button is-increase';
      increaseButton.setAttribute('aria-label', `Add another ${product.title || product.displayName || 'item'}`);
      increaseButton.textContent = '+';

      decreaseButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const resolvedProduct = hasOptions ? this.buildResolvedProduct(card, product) : product;

        if (resolvedProduct) {
          this.adjustVariantQuantity(slotId, resolvedProduct, -1);
        }
      });

      increaseButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const resolvedProduct = hasOptions ? this.buildResolvedProduct(card, product) : product;

        if (resolvedProduct) {
          this.adjustVariantQuantity(slotId, resolvedProduct, 1);
        }
      });

      qtyWrap.appendChild(decreaseButton);
      qtyWrap.appendChild(qtyValue);
      qtyWrap.appendChild(increaseButton);
      card.appendChild(qtyWrap);
    }

    selectButton.addEventListener('click', () => {
      if (hasOptions) {
        this.attemptAddProductFromCard(slotId, card, product);
        return;
      }

      this.toggleSelection(slotId, product, card);
    });

    return card;
  },

  allowsDuplicates: function () {
    return this.config?.constraints?.allowDuplicates === true;
  },

  slotCanPickDuplicates: function (slotId) {
    const slotConfig = this.getSlotConfig(slotId);

    return this.allowsDuplicates() && Boolean(slotConfig && (slotConfig.max ?? 1) > 1);
  },

  countVariantInSlot: function (slotId, variantId) {
    const current = this.selections.get(slotId) || [];

    return current.filter((item) => {
      return item.variantId === variantId;
    }).length;
  },

  syncSlotCardStates: function (slotId) {
    const grid = this.root.querySelector(`[data-slot-grid="${slotId}"]`);

    if (!grid) {
      return;
    }

    const slotConfig = this.getSlotConfig(slotId);
    const showQtyControls = this.slotCanPickDuplicates(slotId);
    const current = this.selections.get(slotId) || [];
    const cards = grid.querySelectorAll('.bundle-product-card');

    cards.forEach((card) => {
      const variantId = card.dataset.variantId || '';
      const product = card.bundleProduct;
      const hasOptions = card.dataset.hasOptions === 'true';
      let count = 0;
      let activeVariantId = variantId;

      if (hasOptions && product) {
        count = this.countProductVariantsInSlot(slotId, product);

        const matchingSelection = current.find((item) => {
          return product.variants.some((variant) => {
            return variant.id === item.variantId;
          });
        });

        if (matchingSelection) {
          activeVariantId = matchingSelection.variantId;
          const matchedVariant = product.variants.find((variant) => {
            return variant.id === matchingSelection.variantId;
          });

          if (matchedVariant && card.bundleOptionState) {
            const optionValues = this.getVariantOptionValues(matchedVariant, product);
            card.bundleOptionState.selectedColor = optionValues.color;
            card.bundleOptionState.selectedSize = optionValues.size;
            this.updateProductOptionUI(card, product);
          }
        }
      } else {
        count = current.filter((item) => {
          return item.variantId === variantId;
        }).length;
      }

      const qtyWrap = card.querySelector('[data-card-qty]');
      const qtyValue = card.querySelector('[data-qty-value]');
      const decreaseButton = card.querySelector('.bundle-product-card-qty-button.is-decrease');
      const increaseButton = card.querySelector('.bundle-product-card-qty-button.is-increase');

      if (count > 0) {
        card.classList.add('is-selected');
      } else {
        card.classList.remove('is-selected');
      }

      if (showQtyControls && qtyWrap && qtyValue) {
        qtyWrap.hidden = count === 0;
        qtyValue.textContent = String(count);

        if (decreaseButton) {
          decreaseButton.disabled = count <= 0;
        }

        if (increaseButton && slotConfig) {
          increaseButton.disabled = current.length >= (slotConfig.max ?? 1);
        }
      }

      if (hasOptions && !activeVariantId && count > 0) {
        card.classList.add('is-selected');
      }
    });
  },

  pulseCardQty: function (slotId, variantId) {
    const grid = this.root.querySelector(`[data-slot-grid="${slotId}"]`);

    if (!grid) {
      return;
    }

    const card = this.findCardByVariantId(grid, variantId);

    if (!card) {
      return;
    }

    card.classList.remove('is-qty-pulse');
    window.requestAnimationFrame(() => {
      card.classList.add('is-qty-pulse');
    });

    window.setTimeout(() => {
      card.classList.remove('is-qty-pulse');
    }, 650);
  },

  adjustVariantQuantity: function (slotId, product, delta) {
    if (!product.available || !product.variantId || delta === 0) {
      return;
    }

    const slotConfig = this.getSlotConfig(slotId);

    if (!slotConfig || !this.slotCanPickDuplicates(slotId)) {
      return;
    }

    const wasComplete = this.isSlotComplete(slotConfig);
    const current = this.selections.get(slotId) || [];
    const variantCountInSlot = this.countVariantInSlot(slotId, product.variantId);

    if (delta > 0) {
      const slotMax = slotConfig.max ?? 1;
      if (current.length >= slotMax) {
        this.showSlotError(slotId, `You can only choose up to ${slotMax} in this slot.`);
        return;
      }

      current.push(this.mapProductSelection(slotId, product));
      this.selections.set(slotId, current);
      this.syncSlotCardStates(slotId);
      this.hideSlotError(slotId);
      this.updateSlotHint(slotId);
      this.finishSelection(slotId, wasComplete);
      return;
    }

    if (variantCountInSlot <= 0) {
      return;
    }

    const removeIndex = current.findIndex((item) => {
      return item.variantId === product.variantId;
    });

    if (removeIndex < 0) {
      return;
    }

    current.splice(removeIndex, 1);
    this.selections.set(slotId, current);
    this.syncSlotCardStates(slotId);
    this.hideSlotError(slotId);
    this.updateSlotHint(slotId);
    this.updateSummary();
  },

  toggleSelection: function (slotId, product, card) {
    if (!product.available || !product.variantId) {
      return;
    }

    const slotConfig = this.getSlotConfig(slotId);
    if (!slotConfig) {
      return;
    }

    const wasComplete = this.isSlotComplete(slotConfig);
    const allowDuplicates = this.allowsDuplicates();
    const canPickDuplicatesInSlot = this.slotCanPickDuplicates(slotId);
    const current = this.selections.get(slotId) || [];
    const variantCountInSlot = this.countVariantInSlot(slotId, product.variantId);
    const variantAlreadySelected = variantCountInSlot > 0;

    if (variantAlreadySelected) {
      if (canPickDuplicatesInSlot) {
        this.updateSlotHint(slotId);
        this.pulseCardQty(slotId, product.variantId);
        return;
      }

      const next = current.filter((item) => {
        return item.variantId !== product.variantId;
      });
      this.selections.set(slotId, next);
      this.syncSlotCardStates(slotId);
      this.hideSlotError(slotId);
      this.updateSlotHint(slotId);
      this.updateSummary();
      return;
    }

    const slotMax = slotConfig.max ?? 1;

    if (slotMax === 1) {
      if (!allowDuplicates) {
        const duplicate = this.findDuplicateVariant(product.variantId, slotId);

        if (duplicate) {
          this.showSlotError(slotId, 'This item is already selected in another slot.');
          return;
        }
      }

      this.selections.set(slotId, [this.mapProductSelection(slotId, product)]);
      this.syncSlotCardStates(slotId);
      this.hideSlotError(slotId);
      this.updateSlotHint(slotId);
      this.finishSelection(slotId, wasComplete);
      return;
    }

    if (current.length >= slotMax) {
      this.showSlotError(slotId, `You can only choose up to ${slotMax} in this slot.`);
      return;
    }

    if (!allowDuplicates) {
      const duplicate = this.findDuplicateVariant(product.variantId, slotId);

      if (duplicate) {
        this.showSlotError(slotId, 'This item is already selected in another slot.');
        return;
      }
    }

    current.push(this.mapProductSelection(slotId, product));
    this.selections.set(slotId, current);
    this.syncSlotCardStates(slotId);
    this.hideSlotError(slotId);
    this.updateSlotHint(slotId);
    this.finishSelection(slotId, wasComplete);
  },

  mapProductSelection: function (slotId, product) {
    return {
      slotId: slotId,
      variantId: product.variantId,
      displayName: product.displayName || product.title || '',
      price: product.price,
      quantity: 1,
      featuredImage: product.featuredImage || null,
    };
  },

  findDuplicateVariant: function (variantId, excludeSlotId) {
    let found = false;

    this.selections.forEach((items, slotId) => {
      if (slotId === excludeSlotId) {
        return;
      }

      items.forEach((item) => {
        if (item.variantId === variantId) {
          found = true;
        }
      });
    });

    return found;
  },

  getSlotConfig: function (slotId) {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return null;
    }

    return this.config.slots.find((slot) => {
      return slot.id === slotId;
    });
  },

  getAllSelections: function () {
    const all = [];

    this.selections.forEach((items) => {
      items.forEach((item) => {
        all.push(item);
      });
    });

    return all;
  },

  getTotalQuantity: function () {
    let total = 0;

    this.getAllSelections().forEach((item) => {
      total += item.quantity;
    });

    return total;
  },

  getCombinedTotalCents: function () {
    let totalCents = 0;

    this.getAllSelections().forEach((item) => {
      const unitCents = this.decimalPriceToCents(item.price);
      totalCents += unitCents * item.quantity;
    });

    return totalCents;
  },

  getPriceDisplayMode: function () {
    return this.config?.priceDisplay?.mode || 'apply_discount';
  },

  getDiscountedTotalCents: function (combinedCents) {
    const discount = this.config?.priceDisplay?.applyDiscount;
    if (!discount) {
      return combinedCents;
    }

    if (discount.type === 'percentage') {
      const percentage = Number(discount.value);
      if (!Number.isFinite(percentage)) {
        return combinedCents;
      }

      return Math.max(0, Math.round(combinedCents * (1 - percentage / 100)));
    }

    if (discount.type === 'fixed') {
      const fixedOffCents = this.decimalPriceToCents(String(discount.value));
      return Math.max(0, combinedCents - fixedOffCents);
    }

    return combinedCents;
  },

  getBundlePriceCents: function () {
    if (this.catalogPriceCents > 0) {
      return this.catalogPriceCents;
    }

    const setPriceAmount = this.config?.priceDisplay?.setPriceAmount;
    if (setPriceAmount) {
      return this.decimalPriceToCents(setPriceAmount);
    }

    return 0;
  },

  updateSummaryTotals: function (combinedCents) {
    const mode = this.getPriceDisplayMode();
    const totalContainers = this.root.querySelectorAll('[data-bundle-total], [data-bundle-mobile-total]');

    totalContainers.forEach((container) => {
      const compareEl = container.querySelector('[data-bundle-compare-total]');
      const saleEl = container.querySelector('[data-bundle-sale-total]');

      if (!saleEl) {
        container.textContent = this.formatCents(combinedCents);
        return;
      }

      let compareCents = combinedCents;
      let saleCents = combinedCents;

      if (mode === 'set_price') {
        saleCents = this.getBundlePriceCents();
        compareCents = combinedCents;
      } else {
        saleCents = this.getDiscountedTotalCents(combinedCents);
        compareCents = combinedCents;
      }

      const hasSavings = saleCents < compareCents;

      if (compareEl) {
        if (hasSavings && mode === 'apply_discount') {
          compareEl.textContent = this.formatCents(compareCents);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      saleEl.textContent = this.formatCents(saleCents);
    });

    const savingsEls = this.root.querySelectorAll('[data-bundle-savings]');
    savingsEls.forEach((savingsEl) => {
      if (!this.showSavingsBanner || mode !== 'set_price') {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
        return;
      }

      const bundlePriceCents = this.getBundlePriceCents();
      const savingsCents = combinedCents - bundlePriceCents;

      if (savingsCents > 0) {
        savingsEl.textContent = `You are saving ${this.formatCents(savingsCents)}`;
        savingsEl.hidden = false;
      } else {
        savingsEl.textContent = '';
        savingsEl.hidden = true;
      }
    });
  },

  isValid: function () {
    if (!this.config) {
      return false;
    }

    const constraints = this.config.constraints || {};
    const totalQty = this.getTotalQuantity();

    if (constraints.minItemsTotal != null && totalQty < constraints.minItemsTotal) {
      return false;
    }

    if (constraints.maxItemsTotal != null && totalQty > constraints.maxItemsTotal) {
      return false;
    }

    if (!Array.isArray(this.config.slots)) {
      return false;
    }

    for (let index = 0; index < this.config.slots.length; index += 1) {
      const slot = this.config.slots[index];
      const slotSelections = this.selections.get(slot.id) || [];
      const slotQty = slotSelections.reduce((sum, item) => {
        return sum + item.quantity;
      }, 0);

      if (slot.required && slotQty === 0) {
        return false;
      }

      if (slot.min != null && slotQty < slot.min) {
        return false;
      }

      if (slot.max != null && slotQty > slot.max) {
        return false;
      }
    }

    if (constraints.allowDuplicates === false) {
      const variantIds = this.getAllSelections().map((item) => {
        return item.variantId;
      });
      const uniqueIds = new Set(variantIds);

      if (uniqueIds.size !== variantIds.length) {
        return false;
      }
    }

    return true;
  },

  updateSummary: function () {
    const totalQty = this.getTotalQuantity();
    const constraints = this.config.constraints || {};
    const minTotal = constraints.minItemsTotal != null ? constraints.minItemsTotal : 0;
    const maxTotal = constraints.maxItemsTotal != null ? constraints.maxItemsTotal : null;
    const combinedCents = this.getCombinedTotalCents();
    const isValid = this.isValid();

    const progressEls = this.root.querySelectorAll('[data-bundle-progress]');
    progressEls.forEach((progressEl) => {
      if (maxTotal != null) {
        progressEl.textContent = `${totalQty} of ${maxTotal} items selected`;
      } else {
        progressEl.textContent = `${totalQty} item${totalQty === 1 ? '' : 's'} selected`;
      }
    });

    const minProgressEls = this.root.querySelectorAll('[data-bundle-min-progress]');
    minProgressEls.forEach((minProgressEl) => {
      if (minTotal > 0 && totalQty < minTotal) {
        minProgressEl.textContent = `Select at least ${minTotal} item${minTotal === 1 ? '' : 's'} to continue`;
        minProgressEl.hidden = false;
      } else {
        minProgressEl.hidden = true;
      }
    });

    this.updateSummaryTotals(combinedCents);

    this.renderSummaryItems();

    const addButtons = this.root.querySelectorAll('[data-bundle-add], [data-bundle-add-mobile]');
    addButtons.forEach((button) => {
      button.disabled = !isValid || this.isAdding || this.bundleUnavailable;

      if (this.isAdding) {
        button.textContent = 'Adding to cart...';
      } else if (this.bundleUnavailable) {
        button.textContent = 'Out of Stock';
      } else {
        const originalLabel = this.addToCartLabels.get(button);
        if (originalLabel) {
          button.textContent = originalLabel;
        }
      }
    });

    this.hideGlobalError();
    this.updateMobileSteps();
    this.updateSlotAccordions();
    this.updateAllSlotHints();
    this.updateIncompleteMessage();
  },

  renderSummaryItems: function () {
    const lists = this.root.querySelectorAll('[data-bundle-summary-list]');
    const allSelections = this.getAllSelections();

    lists.forEach((list) => {
      list.innerHTML = '';

      if (!allSelections.length) {
        const emptyItem = document.createElement('li');
        emptyItem.className = 'bundle-summary-empty';
        emptyItem.textContent = 'Your selections will appear here.';
        list.appendChild(emptyItem);
        return;
      }

      allSelections.forEach((item) => {
        const listItem = document.createElement('li');
        listItem.className = 'bundle-summary-item';

        const info = document.createElement('div');
        info.className = 'bundle-summary-item-info';

        const imageWrap = document.createElement('div');
        imageWrap.className = 'bundle-summary-item-image';

        if (item.featuredImage && item.featuredImage.url) {
          const image = document.createElement('img');
          image.src = item.featuredImage.url;
          image.alt = item.featuredImage.altText || item.displayName || item.title || '';
          image.loading = 'lazy';
          imageWrap.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'bundle-summary-item-image-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          imageWrap.appendChild(placeholder);
        }

        const name = document.createElement('span');
        name.className = 'bundle-summary-item-name';
        name.textContent = item.displayName || item.title || '';

        info.appendChild(imageWrap);
        info.appendChild(name);

        const price = document.createElement('span');
        price.className = 'bundle-summary-item-price';
        price.textContent = this.formatDecimalPrice(item.price);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'bundle-summary-item-remove';
        removeButton.setAttribute('aria-label', `Remove ${item.displayName || item.title || 'item'} from bundle`);
        removeButton.dataset.slotId = item.slotId;
        removeButton.dataset.variantId = item.variantId;

        const removeIconTemplate = this.root.querySelector('[data-bundle-summary-remove-icon-template]');

        if (removeIconTemplate && removeIconTemplate.content) {
          removeIconTemplate.content.childNodes.forEach((node) => {
            removeButton.appendChild(node.cloneNode(true));
          });
        }

        removeButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.removeSummaryItem(item.slotId, item.variantId);
        });

        listItem.appendChild(info);
        listItem.appendChild(price);
        listItem.appendChild(removeButton);
        list.appendChild(listItem);
      });
    });
  },

  removeSummaryItem: function (slotId, variantId) {
    const slotConfig = this.getSlotConfig(slotId);

    if (!slotConfig || !variantId) {
      return;
    }

    const current = this.selections.get(slotId) || [];
    const existingIndex = current.findIndex((selection) => {
      return selection.variantId === variantId;
    });

    if (existingIndex < 0) {
      return;
    }

    current.splice(existingIndex, 1);
    this.selections.set(slotId, current);
    this.syncSlotCardStates(slotId);
    this.hideSlotError(slotId);
    this.updateSlotHint(slotId);
    this.updateSummary();
  },

  updateAllSlotHints: function () {
    if (!this.config || !Array.isArray(this.config.slots)) {
      return;
    }

    this.config.slots.forEach((slot) => {
      this.updateSlotHint(slot.id);
    });
  },

  updateSlotHint: function (slotId) {
    const slot = this.root.querySelector(`[data-slot-id="${slotId}"]`);

    if (!slot) {
      return;
    }

    const hintEl = slot.querySelector('[data-slot-hint]');

    if (!hintEl) {
      return;
    }

    const slotConfig = this.getSlotConfig(slotId);

    if (!slotConfig || !this.slotCanPickDuplicates(slotId)) {
      hintEl.hidden = true;
      hintEl.textContent = '';
      return;
    }

    if (this.isSlotComplete(slotConfig)) {
      hintEl.hidden = true;
      hintEl.textContent = '';
      return;
    }

    const selections = this.selections.get(slotId) || [];
    const remaining = (slotConfig.max ?? 1) - selections.length;

    if (selections.length === 0) {
      hintEl.textContent = 'You can choose the same product more than once in this step. Select an item, then use + to add another.';
      hintEl.hidden = false;
      return;
    }

    if (remaining > 0) {
      hintEl.textContent = `${selections.length} of ${slotConfig.max} selected. Use + on a selected item to add the same product again.`;
      hintEl.hidden = false;
      return;
    }

    hintEl.hidden = true;
    hintEl.textContent = '';
  },

  addToCart: function () {
    if (!this.isValid() || this.isAdding || this.bundleUnavailable) {
      return;
    }

    const token = generateBundleToken();
    const selections = this.getAllSelections();
    const bundleTitle = this.config.title || '';
    const items = [];

    selections.forEach((selection) => {
      const numericId = this.gidToNumericId(selection.variantId);
      if (!numericId) {
        return;
      }

      items.push({
        id: numericId,
        quantity: selection.quantity,
        properties: {
          _bundle_handle: this.handle,
          _bundle_token: token,
          _bundle_title: bundleTitle,
          _slot_id: selection.slotId,
          _source: 'Bundle Builder',
        },
      });
    });

    if (items.length === 0) {
      this.showGlobalError('Bundle configuration is missing selected items.');
      return;
    }

    this.isAdding = true;
    this.updateSummary();
    this.hideGlobalError();

    const body = this.buildCartAddBody(items);
    let addFailed = false;

    fetch(this.cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Add to cart failed (${response.status})`);
        }
        return response.json();
      })
      .then(async () => {
        try {
          await this.openCartDrawer();
        } catch (drawerError) {
          console.warn('BundlePage: could not open cart drawer', drawerError);
        }
      })
      .catch((error) => {
        console.warn('BundlePage: add to cart failed', error);
        addFailed = true;
      })
      .finally(() => {
        this.isAdding = false;
        this.updateSummary();
        if (addFailed) {
          this.showGlobalError('Could not add bundle to cart. Please try again.');
        }
      });
  },

  openCartDrawer: async function () {
    const html = document.documentElement;

    if (typeof Cart !== 'undefined') {
      const cartData = await Cart.getCartItems();

      if (cartData && typeof Cart.syncSidebarLineItems === 'function') {
        await Cart.syncSidebarLineItems(cartData);
      }

      if (typeof Cart.updateCart === 'function') {
        await Cart.updateCart();
      }

      if (html && typeof Utility !== 'undefined' && typeof Utility.hideProgressBarPopup === 'function') {
        Utility.hideProgressBarPopup();
      }

      if (html) {
        html.classList.add('cart-show');
      }

      return;
    }

    if (html) {
      html.classList.add('cart-show');
    }
  },

  buildCartAddBody: function (items) {
    const parts = [];

    items.forEach((item) => {
      parts.push(`items[][id]=${encodeURIComponent(item.id)}`);
      parts.push(`items[][quantity]=${encodeURIComponent(item.quantity)}`);

      Object.keys(item.properties).forEach((key) => {
        const value = item.properties[key];
        parts.push(`items[][properties][${encodeURIComponent(key)}]=${encodeURIComponent(value)}`);
      });
    });

    return parts.join('&');
  },

  gidToNumericId: function (gid) {
    if (!gid) {
      return null;
    }

    if (/^\d+$/.test(gid)) {
      return gid;
    }

    const parts = gid.split('/');
    return parts[parts.length - 1] || null;
  },

  decimalPriceToCents: function (priceString) {
    const amount = parseFloat(priceString);
    if (!Number.isFinite(amount)) {
      return 0;
    }

    return Math.round(amount * 100);
  },

  formatDecimalPrice: function (priceString) {
    return this.formatCents(this.decimalPriceToCents(priceString));
  },

  formatCents: function (cents) {
    const amount = Number(cents) || 0;

    if (typeof Utility !== 'undefined' && typeof Utility.formatLocalizedMoney === 'function') {
      return Utility.formatLocalizedMoney(amount, {
        currency: this.currency || undefined,
      });
    }

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && this.moneyFormat) {
      return Shopify.formatMoney(amount, this.moneyFormat);
    }

    return this.formatMoneyFallback(amount);
  },

  formatMoneyFallback: function (cents) {
    const amount = (cents / 100).toFixed(2);
    const format = this.moneyFormat || '{{amount}}';

    if (format.indexOf('{{') >= 0) {
      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    const storeSymbol = window.SelkirkStore && window.SelkirkStore.currencySymbol;
    if (storeSymbol) {
      return `${storeSymbol}${amount}`;
    }

    return `${amount} ${this.currency}`.trim();
  },

  showSlotError: function (slotId, message) {
    const slot = this.root.querySelector(`[data-slot-id="${slotId}"]`);
    if (!slot) {
      return;
    }

    const errorEl = slot.querySelector('[data-slot-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  },

  hideSlotError: function (slotId) {
    const slot = this.root.querySelector(`[data-slot-id="${slotId}"]`);
    if (!slot) {
      return;
    }

    const errorEl = slot.querySelector('[data-slot-error]');
    if (errorEl) {
      errorEl.hidden = true;
    }
  },

  showGlobalError: function (message) {
    const errorEls = this.root.querySelectorAll('[data-bundle-error]');
    errorEls.forEach((errorEl) => {
      errorEl.textContent = message;
      errorEl.hidden = false;
    });
  },

  hideGlobalError: function () {
    const errorEls = this.root.querySelectorAll('[data-bundle-error]');
    errorEls.forEach((errorEl) => {
      errorEl.hidden = true;
    });
  },
};

/**
 * Generates a unique per-build bundle token with crypto-first and string fallbacks.
 * @returns {string}
 */
function generateBundleToken() {
  try {
    if (typeof window !== 'undefined' && window.crypto) {
      if (typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      if (typeof window.crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => {
          return byte.toString(16).padStart(2, '0');
        }).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      }
    }
  } catch (error) {
    console.warn('BundlePage: crypto token generation failed, using fallback', error);
  }

  const timePart = Date.now().toString(36);
  const randomPart = Math.random().toString(36).slice(2);
  const extraPart = Math.random().toString(36).slice(2);
  return `bundle-${timePart}-${randomPart}${extraPart}`;
}

document.addEventListener('DOMContentLoaded', function () {
  const roots = document.querySelectorAll('[data-bundle-builder]');
  roots.forEach((root) => {
    BundlePage.init(root);
  });
});
